import React, { useState, useEffect } from "react";
import Header from "../Components/Header"; // Import Header
import getLLMResponse from '../utils/getLLMResponse';
import styles from "../Styles/PromptFlowStyles";
import { ClipLoader } from "react-spinners";
import { auth } from "../utils/firebase";
import { submitFeedback, db } from "../utils/firestore";
import { collection, addDoc, query, getDocs, where, updateDoc, deleteDoc, orderBy, limit, doc, serverTimestamp } from "firebase/firestore"; // Import Firestore methods
import { onAuthStateChanged } from "firebase/auth";
import html2pdf from "html2pdf.js";
import { useCallback } from "react"; // Import useCallback
import useAutoLogout from "../hooks/useAutoLogout"; // Import auto logout hook
import useResponsiveStyles from "../hooks/useResponsiveStyles";


function PromptFlow() {
  useAutoLogout(); // ✅ Initialize the auto-logout timer
  
  const [phase, setPhase] = useState("big-idea"); // Set initial phase
  const [bigIdea, setBigIdea] = useState(""); // Track the "big idea"
  const maxWords = 100; // Maximum words for the big idea
  const [loading, setLoading] = useState(false);
  // eslint-disable-next-line no-unused-vars
  const [error, setError] = useState(null);
  // const [email, setEmail] = useState("");
  const [quickAnalysis, setQuickAnalysis] = useState(""); // Store response from handleBigIdeaSubmit
  const [validationStrategy, setValidationStrategy] = useState(""); // Store response from handleDeeperAnalysis
  const [isFeedbackModalOpen, setFeedbackModalOpen] = useState(false);
  const [isSuccessModalOpen, setSuccessModalOpen] = useState(false);
  const [feedback, setFeedback] = useState("");
  const [fbName, setFbName] = useState("");
  const [fbEmail, setFbEmail] = useState("");
  const [fbRole, setFbRole] = useState("");
  const [clarifyingQuestions, setClarifyingQuestions] = useState([]);
  const [clarifyingResponses, setClarifyingResponses] = useState({});
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const maxQuestions = 1; // # of clarifying questions; Adjust as needed
  const [isRefineModalOpen, setRefineModalOpen] = useState(false);
  const [refineFeedback, setRefineFeedback] = useState("");
  const [isSharpenModalOpen, setSharpenModalOpen] = useState(false);
  const [sharpenFeedback, setSharpenFeedback] = useState("");
  const [segmentQuestion, setSegmentQuestion] = useState([]);
  const [segmentResponse, setSegmentResponse] = useState(""); // Ensure it's a string
  const [selectedSegment, setSelectedSegment] = useState(null);
  const [segmentOptions, setSegmentOptions] = useState([]);
  const [deepAnalysis, setDeepAnalysis] = useState(""); // Holds the final deep insight
  const [isConsultModalOpen, setConsultModalOpen] = useState(false);
  const [campaignCode, setCampaignCode] = useState("");
  const [isCampaignValid, setCampaignValid] = useState(null);
  // const [consultData, setConsultData] = useState({ name: "", response1: "", response2: "" });
  const [isSubmitting, setSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [isConsultDetailsModalOpen, setConsultDetailsModalOpen] = useState(false); // New modal state
  const [isAcknowledgementModalOpen, setIsAcknowledgementModalOpen] = useState(false);
  const [isSavedModalOpen, setIsSavedModalOpen] = useState(false);
  const [activeTab, setActiveTab] = useState("new"); // "new" | "saved"
  const [selectedAnalysis, setSelectedAnalysis] = useState(null);
  const [openSection, setOpenSection] = useState("quickAnalysis"); // Default to Quick Analysis
  const [savedAnalyses, setSavedAnalyses] = useState(() => {
    const storedAnalyses = localStorage.getItem("savedAnalyses");
    const storedUser = localStorage.getItem("currentUserEmail"); // ✅ Store user email
    if (storedAnalyses && storedUser === auth.currentUser?.email) {
      return JSON.parse(storedAnalyses); // ✅ Only load if it belongs to the same user
    }
    return []; // ✅ Otherwise, start fresh
  });
  const [userData, setUserData] = useState({
    name: "",
    email: auth.currentUser?.email || "", // Pre-fill with logged-in email
    status: "undergraduate",
    interviews: "yes", // Default to "yes"
  });

  const isMobile = useResponsiveStyles(); // ✅ Detect if screen is mobile
  console.log("Is Mobile?", isMobile);

  const dynamicStyles = {
    container: {
      position: "relative",
      minHeight: "100vh",
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      padding: isMobile ? "10px" : "20px", // ✅ Adjust padding dynamically
      width: "100%",
    },
    pageContainer: {
      display: "flex",
      flexDirection: isMobile ? "column" : "row", // ✅ Switch to column layout on mobile
      justifyContent: "center",
      alignItems: "flex-start",
      width: "100%",
    },
    resultContainer: {
      ...styles.resultContainer,
      width: "100%", // ✅ Prevents external styles from overriding width
      maxWidth: isMobile ? "95%" : "800px", // ✅ Adjust width dynamically
      padding: isMobile ? "10px" : "15px",
      boxSizing: "border-box", // ✅ Ensures padding does not affect width
    },
    newAnalysisContainer: {
      ...styles.newAnalysisContainer,
      maxWidth: isMobile ? "95%" : "1000px", // ✅ Adjust width for mobile
      padding: isMobile ? "10px" : "20px", // ✅ Reduce padding for small screens
    },
    fullScreenView: {
      ...styles.fullScreenView,
      maxWidth: isMobile ? "95%" : "1000px", // ✅ Adjust width for mobile
      padding: isMobile ? "10px" : "20px", // ✅ Reduce padding for small screens
    },
    savedAnalysisContainer: {
      ...styles.savedAnalysisContainer,
      maxWidth: isMobile ? "95%" : "1000px", // ✅ Adjust width for mobile
      padding: isMobile ? "10px" : "20px", // ✅ Reduce padding for small screens
    },
    textarea: {
      ...styles.textarea,
      width: "100%", // ✅ Ensure full width inside its container
      maxWidth: "100%", // ✅ Prevent overflow beyond parent container
      fontSize: isMobile ? "0.85rem" : "0.9rem",
      padding: isMobile ? "8px" : "10px",
      boxSizing: "border-box", // ✅ Ensures padding doesn’t cause overflow
    },
    pageWrapper: {
      ...styles.pageWrapper,
      padding: isMobile ? "10px" : "20px", // ✅ Adjust padding dynamically
    },
    navContainer: {
      display: "flex",
      justifyContent: "center",
      gap: isMobile ? "10px" : "20px",
      width: "100%",
      overflowX: isMobile ? "auto" : "unset", // ✅ Enable scrolling on mobile
    },
    buttonContainer: {
      display: "flex",
      flexDirection: isMobile ? "column" : "row", // ✅ Stack buttons vertically on mobile
      gap: "10px",
      width: "100%",
    },
  };

  
  // 🔥 Wrap fetchSavedAnalyses in useCallback to prevent re-creation
  const fetchSavedAnalyses = useCallback(async () => {
    const user = auth.currentUser;
  
    if (!user || !user.email) {
      console.log("🚨 No authenticated user detected, skipping fetch.");
      return;
    }
  
    try {
      console.log("🔄 Fetching saved analyses for:", user.email);
  
      const analysesRef = collection(db, "savedAnalyses");
  
      // 🔥 Ensure we only fetch the logged-in user's analyses
      const userQuery = query(
        analysesRef,
        where("userEmail", "==", user.email), // ✅ Filters only this user's analyses
        orderBy("timestamp", "asc"),
        limit(5)
      );
  
      const userDocs = await getDocs(userQuery);
      const loadedAnalyses = userDocs.docs.map(doc => ({ id: doc.id, ...doc.data() }));
  
      if (loadedAnalyses.length > 0) {
        setSavedAnalyses(loadedAnalyses);
        localStorage.setItem("savedAnalyses", JSON.stringify(loadedAnalyses)); // ✅ Save to localStorage
        localStorage.setItem("currentUserEmail", user.email); // ✅ Store logged-in user
        console.log("✅ Loaded saved analyses for:", user.email, loadedAnalyses);
      } else {
        console.log("⚠️ No saved analyses found for user:", user.email);
        localStorage.removeItem("savedAnalyses"); // ✅ Clear if none found
      }
  
    } catch (error) {
      console.error("🔥 Error loading saved analyses:", error);
    }
  }, [setSavedAnalyses]);
  
  // 🔥 Fetch analyses when user logs in
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      if (user) {
        console.log("✅ User logged in:", user.email);

        // CLEAR OLD USER DATA from local storage
        setSavedAnalyses([]); 
        localStorage.removeItem("savedAnalyses");

        fetchSavedAnalyses(); // ✅ Fetch only if authentication is ready
      } else {
        console.log("🚨 No user detected, clearing saved analyses.");
        setSavedAnalyses([]); 
        localStorage.removeItem("savedAnalyses");
      }
    });
  
    return () => unsubscribe();
  }, [fetchSavedAnalyses]);

  // Clear saved analyses on browser close/reload
  useEffect(() => {
    const handleBeforeUnload = () => {
      console.log("🔄 Clearing saved analyses on browser close/reload.");
      localStorage.removeItem("savedAnalyses"); // ✅ Clears saved analyses on browser close
    };
  
    window.addEventListener("beforeunload", handleBeforeUnload);
  
    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, []);

  // 🔥 Load saved analyses from localStorage on component mount
  useEffect(() => {
    const storedAnalyses = localStorage.getItem("savedAnalyses");
    if (storedAnalyses) {
      setSavedAnalyses(JSON.parse(storedAnalyses));
      console.log("🔄 Loaded saved analyses from localStorage");
    }
  }, []);

  // Disable pointer events when loading
  useEffect(() => {
    document.body.style.pointerEvents = loading ? "none" : "auto";
    return () => {
      document.body.style.pointerEvents = "auto"; // Reset when loading is false
    };
  }, [loading]);

  useEffect(() => {
    localStorage.setItem("savedAnalyses", JSON.stringify(savedAnalyses));
  }, [savedAnalyses]);

  // Save analysis to state
  const handleSaveAnalysis = async () => {
    if (!quickAnalysis || !deepAnalysis || !validationStrategy) {
      alert("Analysis is incomplete. Please generate all sections before saving.");
      return;
    }

    const userEmail = auth.currentUser?.email;
    if (!userEmail) {
      alert("User is not logged in.");
      return;
    }

    setActiveTab("saved"); // Switch to the Saved Analyses tab
    setLoading(true);

    try {
      const timestamp = new Date().toISOString();
      const newAnalysis = {
        title: `Deep Dive - ${new Date().toLocaleString()}`,
        quickAnalysis,
        deepAnalysis,
        validationStrategy,
        elevatorPitch: "Generating pitch...",
        timestamp,
        userEmail, // Associate with logged-in user
      };

      const analysesRef = collection(db, "savedAnalyses");

      // 🔍 Fetch user's existing saved analyses (limit 5, sorted oldest first)
      const userQuery = query(
        analysesRef,
        where("userEmail", "==", userEmail),
        orderBy("timestamp", "asc"), // ✅ Sort by oldest first (FIFO)
        limit(5)
      );
      const userDocs = await getDocs(userQuery);
      const savedCount = userDocs.size;

      if (savedCount >= 5) {
        // 🔥 FIFO: Delete the **oldest** entry
        const oldestDoc = userDocs.docs[0]; // First document is the oldest
        await deleteDoc(doc(db, "savedAnalyses", oldestDoc.id));
        console.log("🗑️ Deleted oldest analysis to maintain the limit of 5.");
      }

      // 🔥 Generate elevator pitch based on deepAnalysis
      const pitchPrompt = `
        Generate a **1-sentence description** summarizing this startup idea concisely:
        
        **Deep Analysis Summary:**  
        ${deepAnalysis}
        
        - The description should be **simnple and clear**.  
        - It should indicate: **What the startup does, who it serves, and why it's unique**.
        - Format: **"This [product / solution] helps [target customer] solve [problem] so that [key benefit]."**
      `;

      const elevatorPitch = await getLLMResponse(
        [{ role: "system", content: "You are an an experienced business consultant." }],
        pitchPrompt,
        "elevator-pitch"
      );

      // ✅ Save analysis to Firestore
      const docRef = await addDoc(analysesRef, { ...newAnalysis, elevatorPitch });
      console.log("✅ Saved analysis:", newAnalysis);

      // ✅ Update local state with FIFO behavior
      setSavedAnalyses(prev => {
        const updatedAnalyses = [...prev, { ...newAnalysis, elevatorPitch, id: docRef.id }];
        if (updatedAnalyses.length > 5) {
          updatedAnalyses.shift(); // 🔥 Remove the oldest entry from local state
        }
        return updatedAnalyses;
      });

      setSelectedAnalysis({ ...newAnalysis, elevatorPitch, id: docRef.id });

    } catch (error) {
      console.error("🔥 Error saving analysis:", error);
      alert("Failed to save analysis. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  // Generate executive summary
  const generateExecutiveSummary = async (quickAnalysis, selectedSegment, deepAnalysis ) => {
    const prompt = `
      Create a structured **Executive Summary** of the startup proposal based on the following details:
      
      ### **Analysis Details**
      - **Quick Analysis**:  
        ${quickAnalysis}
      
      - **Selected Segment**:  
        ${selectedSegment}
      
      - **Deep Analysis**:  
        ${deepAnalysis}
      
      ---
      
      ### **Guidelines for the Executive Summary**
      - **Concise (under 200 words)**
      - **Clearly outlines the startup's core value proposition and target market**
      - **Highlights the opportunity, differentiation, and potential challenges**
      - **Provides key insights and strategic recommendations**
      
      ### **Output Format**
      ---
      #### **Executive Summary**  
      **1. Overview**  
      - Brief introduction to the startup concept, including its purpose and market focus.
      
      **2. Core Value Proposition**  
      - What problem does this startup solve?  
      - What is its unique advantage compared to competitors?  
      
      **3. Target Market**  
      - Who are the primary customers?  
      - Why is this market attractive?  
      
      **4. Opportunity & Differentiation**  
      - Key market trends and gaps this startup is addressing.  
      - Competitive positioning and how it stands out.  
      
      **5. Key Challenges**  
      - Risks, barriers to entry, or potential execution challenges.  
      ---
    `;
  
    const messages = [{ role: "system", content: "You are an expert business analyst summarizing startup strategies." }];
  
    // 🔥 Fetch the full response at once (no streaming)
    const fullSummary = await getLLMResponse(messages, prompt, "executive-summary");
  
    return fullSummary; // Return the complete summary
  };

  const handleDeleteAnalysis = async (id) => {
    setSavedAnalyses(prevAnalyses => prevAnalyses.filter(analysis => analysis.id !== id));
    setSelectedAnalysis(null); // ✅ Return to list view after deletion
    try {
      await deleteDoc(doc(db, "savedAnalyses", id));
      console.log("🗑️ Successfully deleted analysis:", id);
    } catch (error) {
      console.error("🔥 Error deleting analysis:", error);
    }
  };

  // Validate consult campaign code
  const validateCampaign = async () => {
    if (!campaignCode.trim()) return;
  
    try {
      const normalizedCode = campaignCode.trim().toLowerCase(); // Convert input to lowercase
      console.log("Checking campaign code:", normalizedCode); // Debug log
  
      // Query Firestore for campaigns where the `code` field matches (case-insensitive)
      const campaignsRef = collection(db, "campaigns");
      const campaignsQuery = query(campaignsRef, where("code", "==", normalizedCode));
      const campaignsSnap = await getDocs(campaignsQuery);
  
      console.log("Fetched documents count:", campaignsSnap.size); // Debug: Check number of docs
  
      if (!campaignsSnap.empty) {
        let isValid = false;
  
        campaignsSnap.forEach((doc) => {
          const campaignData = doc.data();
          console.log("Matching campaign found:", doc.id, campaignData); // Debug log
  
          // Check for valid code & active campaign
          if (campaignData.code.toLowerCase() === normalizedCode && campaignData.active) {
            isValid = true;
          }
        });
  
        if (isValid) {
          console.log("✅ Campaign is valid and active!");
          setCampaignValid(true);
          setConsultModalOpen(false); // Close campaign code modal
          setConsultDetailsModalOpen(true); // Open the new consult details modal
          return;
        }
      }
  
      console.log("❌ No valid campaign found.");
      setCampaignCode(""); // 🔥 Clear input field
      setErrorMessage("Invalid or inactive request code. Please try again.");
      setShowErrorModal(true);
      
      // Close the consultation details modal (if open) to prevent multiple popups
      setConsultDetailsModalOpen(false);
      
      } catch (error) {
        console.error("🔥 Firestore error:", error);
        setCampaignCode(""); // 🔥 Clear input field
        setErrorMessage("Error validating request. Please try again.");
        setShowErrorModal(true);
      
        // Ensure no duplicate modals open
        setConsultDetailsModalOpen(false);
      }
  };;

  // Handle consult request submission
  const submitConsultRequest = async () => {
    if (!userData.name.trim()) { // 🔍 Check if name is empty
      alert("Please enter your name before submitting.");
      return;
    }

    if (!selectedAnalysis) {
      alert("No analysis selected for consultation.");
      return;
    }

    setSubmitting(true);
    try {
      // 🔥 Step 1: Generate Executive Summary
      const executiveSummary = await generateExecutiveSummary(
        selectedAnalysis.quickAnalysis,
        selectedAnalysis.selectedSegment || "N/A", // Handle missing segment
        selectedAnalysis.deepAnalysis
      );

      // 🔍 Step 2: Check for an existing request (same email + not responded)
      const requestsRef = collection(db, "requests");
      const existingQuery = query(requestsRef, where("email", "==", userData.email), where("responded", "==", false));
      const existingSnap = await getDocs(existingQuery);

      // 🔥 Step 3: Create request data
      const requestData = {
        name: userData.name,
        email: userData.email,
        status: userData.status,
        interviews: userData.interviews,
        campaignCode,
        executiveSummary, // ✅ Store the generated summary
        timestamp: serverTimestamp(),
        responded: false, // Default value
      };

      if (!existingSnap.empty) {
        // 📌 Step 4: Update existing request if found
        const existingDoc = existingSnap.docs[0].ref;
        await updateDoc(existingDoc, requestData);
        console.log("✅ Updated existing consultation request:", requestData);
      } else {
        // 🆕 Step 5: Create a new request if none exists
        await addDoc(requestsRef, requestData);
        console.log("✅ Created new consultation request:", requestData);
      }

      // 🎉 Step 6: Close the form and show acknowledgment
      setConsultDetailsModalOpen(false); // Close request details modal
      setIsAcknowledgementModalOpen(true); // Show acknowledgment modal

    } catch (error) {
      console.error("🔥 Error submitting consult request:", error);
      alert("Failed to submit request. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  
  // Handle feedback submission
  const handleFeedbackSubmit = async (e) => {
    e.preventDefault();
  
    // Create an object with the feedback data
    const feedbackData = {
      feedback,
      fbName,
      fbEmail,
      fbRole,
    };
  
    try {
      // Submit feedback using the submitFeedback function
      await submitFeedback(feedbackData);
  
      // Reset form fields after submission
      setFeedback("");
      setFbName("");
      setFbEmail("");
      setFbRole("");
  
      // Close the feedback modal and show the success modal
      setFeedbackModalOpen(false);
      setSuccessModalOpen(true);
    } catch (error) {
      console.error("Feedback submission failed:", error);
      alert("An error occurred while submitting your feedback. Please try again.");
    }
  };

  // Format streamed LLM response for display
  const formatStreamedText = (text) => {
    return text
      .split("\n")
      .filter(line => line.trim() !== "") // Remove empty lines
      .map((line, index) => {
        let cleanLine = line.replace(/\*\*/g, ""); // Remove bold markers
  
        // Convert "### Section Headers" to bold text
        if (/^###\s/.test(cleanLine)) {
          return (
            <div key={index} style={{ 
              fontWeight: "bold", 
              fontSize: "1.2em", 
              marginTop: "15px", 
              paddingBottom: "5px",
              borderBottom: "2px solid #ddd",
              display: "block"
            }}>
              {cleanLine.replace(/^###\s*/, "").trim()}
            </div>
          );        }
  
        // Format numbered lists with proper hanging indents
        if (/^\d+\.\s+/.test(cleanLine)) {
          return (
            <div key={index} style={{ 
              marginLeft: "1em", 
              textIndent: "-1em", 
              paddingLeft: "1.5em",
              fontSize: "1em",
              fontWeight: "500"
            }}>
              {cleanLine}
            </div>
          );
        }
  
        // Format sub-bullets (e.g., "- Bullet point")
        if (/^\s*-\s+/.test(cleanLine)) {
          return (
            <div key={index} style={{ 
              marginLeft: "2em", 
              textIndent: "-1.5em", 
              paddingLeft: "2em",
              fontSize: "0.95em"
            }}>
              {cleanLine}
            </div>
          );
        }

        //  Highlight Key Insights with Icons & Styling  
        if (/^\*\*(.*?)\*\*/.test(cleanLine)) {
          return (
            <div key={index} style={{ fontWeight: "bold", fontSize: "1em", color: "#333" }}>
              {cleanLine.replace(/\*\*/g, "")}
            </div>
          );
        }
  
        // Default paragraph formatting
        return <div key={index}>{cleanLine}</div>;
      });
  };

  // Format saved analysis for display
  const formatSavedAnalysis = (text) => {
    if (!text || typeof text !== "string") {
      console.warn("Invalid text received for formatting:", text);
      return <p style={{ fontStyle: "italic", color: "#888" }}>No content available.</p>;
    }
  
    console.log("Formatting saved analysis:", text); // Debugging log
  
    return text
      .split("\n")
      .filter(line => line.trim() !== "") // Remove empty lines
      .map((line, index) => {
        let cleanLine = line.replace(/\*\*/g, ""); // Remove ** bold markers
  
        // Convert "#### Section Headers" to styled headers
        if (/^####\s/.test(cleanLine)) {
          return (
            <div key={index} style={{ 
              fontWeight: "bold", 
              fontSize: "1.2em", 
              marginTop: "15px", 
              paddingBottom: "5px",
              borderBottom: "2px solid #ddd",
              display: "block"
            }}>
              {cleanLine.replace(/^####\s*/, "").trim()}
            </div>
          );
        }
  
        // Format numbered lists with proper indentation
        if (/^\d+\.\s+/.test(cleanLine)) {
          return (
            <div key={index} style={{ 
              marginLeft: "1em", 
              textIndent: "-1em", 
              paddingLeft: "1.5em",
              fontSize: "1em",
              fontWeight: "500"
            }}>
              {cleanLine}
            </div>
          );
        }
  
        // Format bullet points
        if (/^\s*-\s+/.test(cleanLine)) {
          return (
            <div key={index} style={{ 
              marginLeft: "2em", 
              textIndent: "-1.5em", 
              paddingLeft: "2em",
              fontSize: "0.95em"
            }}>
              {cleanLine}
            </div>
          );
        }
  
        // Default paragraph formatting
        return <p key={index}>{cleanLine}</p>;
      });
  };

  // Limit big idea to 150 words
  const handleTextareaChange = (e) => {
    const inputText = e.target.value;
    const words = inputText.trim().split(/\s+/);
    if (words.length > maxWords) {
      // Truncate to 100 words
      setBigIdea(words.slice(0, maxWords).join(" "));
    } else {
      setBigIdea(inputText);
    }
  };

  // Generate clarifying questions 
  const generateNextQuestion = async (bigIdea, clarifyingResponses) => {
    const responsesSummary = Object.values(clarifyingResponses)
      .map((response, index) => `**Q${index + 1}:** ${response}`)
      .join("\n\n");
  
      const prompt = `
      Given the following startup idea and responses, generate the **next best clarifying question** to refine the idea.  
      The goal is to critically assess:
      - The **problem**: How urgent, costly, and widespread is it? What existing alternatives exist?
      - The **ideal customer**: Who suffers most? Who would pay? What behavioral or financial barriers exist?
      - The **unique solution**: How is this differentiated? Why is it sustainable?
      - The **business feasibility**: What are the biggest cost drivers? What is the potential revenue model?
      
      ---
      
      **Startup Idea**:
      ${bigIdea}
      **Prior Responses**:
      ${responsesSummary || "None yet."}
      
      ---
      
      ### **Instructions for the question:**
      - **Identify critical knowledge gaps**:
        - If the problem lacks urgency, ask about economic impact or alternative solutions.
        - If customer behavior is vague, ask about willingness to pay, switching costs, or discovery mechanisms.
        - If differentiation is unclear, ask about defensibility (IP, network effects, first-mover advantage).
        - If feasibility is unclear, ask about business model sustainability.
      
      - **Ensure the question is**:  
        - **Open-ended** (avoids yes/no answers).  
        - **Specific** (helps uncover deeper insights into the startup idea).  
        - **Engaging** (encourages thoughtful responses).
      
      ---
      
      ### **Format:**
      - Generate **one** concise, engaging, and high-impact clarifying question.        
      `;
  
    const messages = [{ role: "system", content: "You are a early-stage startup mentor helping an entrepreneur refine their startup idea." }];
  
    const response = await getLLMResponse(messages, prompt, "clarifying-questions");
    return response;
  };

  // Analyse the "big idea" (prompt)
  const handleBigIdeaSubmit = async ({ bigIdea, clarifyingResponses }) => {
    if (!bigIdea.trim() || Object.values(clarifyingResponses).every((response) => !response.trim())) {
      setError("Please ensure the Big Idea and clarifying responses are provided.");
      return;
    }
  
    setError(null);
    setQuickAnalysis(""); // Clear previous response
    setPhase("big-idea-analysis"); // Move to analysis phase immediately
  
    try {
      const responsesSummary = Object.entries(clarifyingResponses)
        .map(([index, response]) => `**Clarifying Question ${parseInt(index, 10) + 1}:** ${response}`)
        .join("\n\n");
  
      const prompt = `
        You are an experienced startup mentor and data-driven analyst. Your goal is to provide **clear, structured, and actionable insights** that help founders evaluate their ideas effectively.

        Using the following inputs, analyze and assess the startup idea:
        **Big Idea**:
        ${bigIdea}
        **Clarifying Responses**:
        ${responsesSummary}
        ---
        
        **Analysis Framework** - Your evaluation should be well-structured and backed by **data-driven insights**.
        Provide responses in the following format:
        ---  

        ### The Big Idea
        Summarize the startup concept in **1-2 sentences**, clearly outlining:
        - **The problem** the startup aims to solve.
        - **The proposed solution** and how it addresses the problem.
        - **The ideal customer** who would benefit from the solution.

        ### Opportunity Landscape
        Provide an overview of the market opportunity in the following format:
        1. **Market Size**
          - Estimate the **Total Addressable Market (TAM)** in **SGD**, using real-world data or industry benchmarks.
          - Clearly explain the basis for the estimation, referencing relevant industry trends or statistics.
        2. **Key Trends**
          - Identify **three** major industry or market trends that impact the startup opportunity.
          - Each trend should be formatted as a **concise bullet point**, highlighting its relevance.
        3. **Challenges & Risks**
          - Outline **three** significant challenges or risks that could impact success.
          - Use **concise bullet points**, covering **regulatory, technical, or competitive risks**.
        
        ### Potential Customer Segments
        Identify **three distinct customer segments**, ensuring:
        - **Clear problem fit** - How urgent or painful is the problem?
        - **Market accessibility** - How easy is it to reach and acquire these customers?
        - **Revenue potential** - How willing are they to pay for a solution?
        - **Be creative** - Include at least **one overlooked or unconventional segment**, but **do not** explicitly label it as "non-obvious."

        Present the customer segments in order of attractiveness, using concise bullet points:
        1. **[Most promising customer segment]** - Briefly explain why this segment is a strong fit.
        2. **[Second promising customer segment]** - Same structure as above.
        3. **[Third promising customer segment]** - Same structure as above.

        ### Recommended Beachhead
        Identify the **most promising initial customer segment** and provide a **concise, well-structured** justification in 2-3 sentences.  
        - Explain **why this segment is the best starting point**, considering its market size, budget, and adoption likelihood.  
        - Highlight how early adopters within this segment can accelerate traction and drive broader adoption.  
        - Ensure the reasoning is naturally embedded without using explicit labels like "Quantitative reasoning" or "Early traction potential." 

        Ensure the recommendation is **specific, well-supported, and practical**.
      `;
  
      const messages = [{ role: "system", content: "Act as a rigorous startup analyst. Prioritize data-backed insights, flag assumptions, and maintain an objective tone. Focus on actionable recommendations over theoretical analysis." }];
  
      // Streaming response directly into quickAnalysis
      await getLLMResponse(messages, prompt, "big-idea", setQuickAnalysis);

    } catch (error) {
      setError("Error analyzing the idea.");
      console.error(error);
    } 
  };

  // Refine the Quick Analysis
  const handleRefineSubmit = async () => {
    if (!refineFeedback.trim()) {
      alert("Please enter feedback to refine your idea.");
      return;
    }

    setLoading(true);
    setRefineModalOpen(false);
    setError(null);

    // Store the current quickAnalysis in a local variable BEFORE clearing it
    const previousAnalysis = quickAnalysis; 
    
    // Clear quickAnalysis before streaming starts
    setQuickAnalysis("");

    try {
      const prompt = `
        Refine the Quick Analysis based on the following feedback:
        **Feedback**:
        ${refineFeedback}
        **Original Quick Analysis**:
        ${previousAnalysis}

        Provide the refined analysis in the same structured format as the original Quick Analysis.
      `;

      const messages = [{ role: "user", content: prompt }];

      await getLLMResponse(messages, prompt, "refine-analysis", setQuickAnalysis); // Streaming
      setRefineFeedback(""); // Clear input

    } catch (error) {
      setError("Error refining the Quick Analysis.");
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (quickAnalysis && quickAnalysis.includes("### Potential Customer Segments")) {
      try {
        const segmentsSection = quickAnalysis.match(/### Potential Customer Segments([\s\S]*?)###/);
        if (!segmentsSection) {
          console.warn("No 'Potential Customer Segments' section found in quickAnalysis.");
          setSegmentOptions([]); // Reset options if the section is missing
          return;
        }
  
        const segments = segmentsSection[1]
        .split("\n")
        .filter((line) => line.trim().match(/^\d+\.\s+\*\*(.*?)\*\*/)) // Match numbered list with bold titles
        .map((line) => {
          const match = line.match(/^\d+\.\s+\*\*(.*?)\*\*/); // Extract title from "**Title** - Description"
          return match ? match[1].trim() : line.trim(); // Only keep the title
        });
  
        console.log("Extracted Segments:", segments); // Debugging output
        setSegmentOptions(segments);
      } catch (error) {
        console.error("Error parsing customer segments:", error);
      }
    }
  }, [quickAnalysis]);

  // Generate  segment clarifying question
  const generateSegmentQuestion = async (segment) => {
    setLoading(true);
    setError(null);
    setSegmentQuestion(""); // Clear existing question

    try {
      const segmentPrompt = `
      The goal is to refine the **customer segment analysis** for **${segment}** before finalizing the startup’s go-to-market approach.
    
      **Context:**  
      - **Startup Idea:** ${quickAnalysis}  
      - **Selected Customer Segment:** ${segment}  
    
      ### **Guidelines for the Clarifying Question:**  
      - **Assess missing information dynamically**:
        1. **If location is unspecified**, ask about the geographic focus (e.g. city, region, country).  
        2. **If the value proposition is vague**, ask how the prospective customers will benefit from the solution.  
        3. **If affordability or willingness-to-pay is missing**, ask what the prospective customers currently spend on solutions.  
        4. **If go-to-market channels are unclear**, ask how the prospective customers usually discover and adopt new solutions.  
    
      - **Format:**  
        - Provide only **one** open-ended, specific, and actionable question that helps refine the market analysis.  
    `;

      const messages = [{ role: "system", content: "You are an expert in market research and startup validation." }];

      const response = await getLLMResponse(messages, segmentPrompt, "segment-question");
      setSegmentQuestion(response); // Store question

    } catch (error) {
      setError("Error generating segment question.");
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  // Handle segement response submission
  const handleSegmentResponseSubmit = async () => {
    if (!segmentResponse.trim()) {
      setError("Please provide a response before proceeding.");
      return;
    }
  
    setLoading(true);
    setError(null);
  
    try {
      await generateSegmentInsight(selectedSegment, segmentResponse); // Generate deep insight
      setPhase("insight"); // Move to insight phase
    } catch (error) {
      setError("Error generating insight.");
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  // Sharpen the Deep Analysis
  const handleSharpenSubmit = async () => {
    if (!sharpenFeedback.trim()) {
      alert("Please enter feedback to refine your idea.");
      return;
    }
  
    setLoading(true); // Show loading spinner
    setSharpenModalOpen(false); // Close modal
    setError(null); // Clear previous errors

    // Store the current DeepAnalysis in a local variable BEFORE clearing it
    const previousAnalysis = deepAnalysis;

    // Clear DeepAnalysis before streaming starts
    setDeepAnalysis("");
  
    try {
      const prompt = `
        Sharpen the Deep Segment Analysis based on the following feedback:
        **Feedback**:
        ${sharpenFeedback}
        **Original Deep Segment Analysis**:
        ${previousAnalysis}
  
        Provide the sharpened Analysis in the same structured format as the original Deep Analysis.
      `;
  
      const messages = [{ role: "user", content: prompt }];

      // Streaming response directly into DeepAnalysis
      await getLLMResponse(messages, prompt, "sharpen-analysis", setDeepAnalysis);
      setSharpenFeedback(""); // Clear feedback
      
    } catch (error) {
      setError("Error sharpening the Deep Analysis.");
      console.error(error);
    } finally {
      setLoading(false); // Stop loading spinner
    }
  };

  // Generate deep analysis for the selected segment
  const generateSegmentInsight = async (segment, userResponse) => {
    setDeepAnalysis("");
    setPhase("insight"); // Ensure UI updates immediately
  
    try {
      const insightPrompt = `
        You are a startup market analyst tasked with analysisng the launch market segment for a proposed startup
        Using the initial analysis: **${quickAnalysis}**, the selected launch segment: **${segment}**, and the user's clarifying responses: **${userResponse}**, conduct a **deep market viability analysis** for launching the startup.
  
        **Analysis guidelines**:
        1. **Market Viability**  
          - **Market Size**: Current size + growth trends (emerging/mature/declining)  
          - **Segment Suitability**: Alignment with startup's core capabilities  
        2. **Customer Insights**  
          - **Pain Points**: Urgency + economic impact of current challenges  
          - **Psychographics**: Decision-making drivers + tech adoption patterns  
        3. **Competitive Landscape**  
          - **Direct Competitors**: Top 3 with SWOT-style analysis (strengths/weaknesses)  
          - **Substitutes**: Manual workarounds/DIY solutions  
        4. **Differentiation Opportunities**  
          - **Strategic Gaps**: Unmet needs in competitors' offerings  
          - **Positioning Strategies**: Price differentiation, experience enhancement, niche targeting  
  
        **Output Format:** 
        
        ### **Segment Overview**
        [2-3 sentences: Market size (SGD/units), growth trends, market maturity stage]

        ### **Customer Profile**
        [2-3 sentences: Psychographics, spending patterns, decision-making triggers]  

        ### **Value Proposition**
        [Generate the jobs-to-be-done, pains, and gains for the selected customer segment, with 3 succinct bullet points each.]
        1. **Jobs-to-be-Done**
          - [Job 1]
          - [Job 2]
          - [Job 3]
        2. **Pains**
          - [Pain 1]
          - [Pain 2]
          - [Pain 3]
        3. **Gains**
          - [Gain 1]
          - [Gain 2]
          - [Gain 3]

        ### **Direct Competitors**
        **Name** the top 3 **direct competitors** in the selected segemnt **${segment}**. 

        **Format:**  
        1. **Name**: [Competitor A]  
        - **Offering**: a short product/service description
        - **Positioning**: a brief outline of the unique selling proposition

        2. **Name**: [Competitor B]  
        - **Offering**: a short product/service description
        - **Positioning**: a brief outline of the unique selling proposition

        3. **Name**: [Competitor C]  
        - **Offering**: a short product/service description
        - **Positioning**: a brief outline of the unique selling proposition

        ### **Substitutes & Alternatives**  
        [1-2 sentences: identify possible alternative solutions: manual workarounds, DIY solutions, substitutes, etc.]
      
        ### **Differenrtiation Opportunities**
        [Identify actionable positioning strategies for the startup based on identified market gaps]
          1. **Price strategy**
          - premium vs. low-cost alternative
          2. **Experience differentiation**
          - faster, better, more convenient
          3. **Niche capture**
          - underserved sub-segments or specialized needs
      `;
  
      const messages = [{ role: "system", content: "Act as a rigorous market analyst. Focus on data-backed insights, competitor weaknesses, and actionable differentiation strategies. Prioritize quantitative metrics (market size, growth rates) and strategic gaps over theoretical analysis." }];

      // Streaming response directly into DeepAnalysis
      await getLLMResponse(messages, insightPrompt, "deep-analysis", setDeepAnalysis);

    } catch (error) {
      setError("Error generating deep analysis.");
      console.error(error);
    }
  };

  // Generate validation strategy
  const generateValidationStrategy = async () => {
    setValidationStrategy(""); // Clear previous response
    setPhase("validation-strategy");
    setLoading(true);
    setError(null);
  
    try {
      const validPrompt = `
        You are a seasoned startup mentor specializing in market validation strategies. 
        Using insights from the deep analysis: **${deepAnalysis}** develop a practical **validation plan** to confirm the startup's market opportunity in the selected segment: **${selectedSegment}**.
        The response should focus on **formulating a practical interview and testing plan** designed to validate customer demand, adoption potential, and market feasibility.
        Provide the strategy in the following structured format:
  
        ### **Hypotheses to Validate**
        List **three critical assumptions** that must be tested, ensuring each is:
        - Clearly stated as a **testable hypothesis**.  
        - Justified with its relevance to product success and market demand.  
        - Concise and impactful, avoiding unnecessary details.

        **Format**:
        1. **Hypothesis 1**: [Assumption to test]
        2. **Hypothesis 2**: [Assumption to test]
        3. **Hypothesis 3**: [Assumption to test]

        ### **Interview Prospects**
        Identify **three categories of interviewees** who can provide critical insights:
        1. **Potential early adopters** (e.g., industry professionals, tech-savvy users)
          - Why: They can validate initial demand.              
        2. **Decision-makers** (e.g., procurement managers, budget holders)
          - Why: They control purchase decisions.              
        3. **Industry influencers / experts** (e.g., analysts, consultants)
          - Why: They provide market-wide perspective.
  
        ### **Sample Questions**
        Generate **5 open-ended interview questions** covering:  
        1. **Problem severity**  
        2. **Current solution limitations**  
        3. **Willingness to pay** (with price anchoring)  
        4. **Adoption barriers**  
        5. **Feature prioritization**   
      `;
  
      const messages = [{ role: "system", content: "Act as a startup validation expert. Focus on creating **actionable, low-cost strategies** that prioritize data-driven insights. Emphasize practical methods (interviews, surveys) over theoretical analysis. Ensure questions are open-ended and avoid leading phrasing." }];
  
      // Streaming response directly into validationStrategy
      await getLLMResponse(messages, validPrompt, "validation-strategy", setValidationStrategy);


    } catch (error) {
      setError("Error generating validation strategy.");
      console.error(error);
    } finally {
      setLoading(false); // ✅ Ensure loading is set to false when done
    }
  };

  // Reset to the first screen
  const resetToBigIdea = () => {
    setBigIdea("");          // Reset Big Idea input
    setQuickAnalysis("");    // Reset Quick Analysis content
    setDeepAnalysis("");     // Reset Deep Analysis content
    setValidationStrategy(""); // Reset Validation Strategy content
    setClarifyingQuestions([]);        // Clear all clarifying questions
    setClarifyingResponses({});        // Clear all user responses
    setCurrentQuestion(0);             // Reset question index to the first question
    setSegmentQuestion("");
    setSegmentResponse(""); // Reset segment response             
    setSelectedSegment(null); // Reset selected segment
    setSegmentOptions([]);    // Reset segment options
    setRefineFeedback("");    // Reset refine feedback
    setSharpenFeedback("");   // Reset sharpen feedback
    setError(null);          // Clear any error messages
    setLoading(false);       // Ensure loading is stopped
    setPhase("big-idea");    // Reset to the initial phase
  };

  // Download Analysis PDF
  const handleDownloadPDF = () => {
    if (!selectedAnalysis) {
      alert("No analysis selected for download.");
      return;
    }
  
    const email = auth.currentUser?.email || "User Email Not Available";
    const date = new Date().toLocaleDateString();
  
    // Create a container div
    const contentContainer = document.createElement("div");
  
    // Title
    const title = document.createElement("h1");
    title.innerText = "askAIvan Analysis";
    title.style.textAlign = "center";
  
    // User Info
    const userInfo = document.createElement("p");
    userInfo.innerText = `User: ${email}\nDate: ${date}`;
    userInfo.style.textAlign = "center";
  
    contentContainer.appendChild(title);
    contentContainer.appendChild(userInfo);
  
    // Function to format text properly
    const formatText = (text) => {
      return text
        .replace(/###\s?/g, "") // Remove "###" headers
        .replace(/\*\*(.*?)\*\*/g, "$1") // Remove "**bold markers**"
        .replace(/^- /gm, "• ") // Convert dashes into bullet points
        .replace(/(\d+)\.\s/g, "\n$1. "); // Ensure numbered lists stay numbered with proper spacing
    };
  
    // Function to create section elements
    const addSection = (titleText, content) => {
      if (content && content.trim()) {
        const sectionTitle = document.createElement("h2");
        sectionTitle.innerText = titleText;
        sectionTitle.style.marginTop = "20px";
  
        const sectionContent = document.createElement("p");
        sectionContent.innerText = formatText(content); // Apply formatting
  
        contentContainer.appendChild(sectionTitle);
        contentContainer.appendChild(sectionContent);
      }
    };
  
    // Use selectedAnalysis data instead of current analysis
    addSection("Quick Analysis", selectedAnalysis.quickAnalysis);
    addSection("Deep Analysis", selectedAnalysis.deepAnalysis);
    addSection("Validation Strategy", selectedAnalysis.validationStrategy);
  
    // Ensure there is content before generating the PDF
    if (contentContainer.childNodes.length < 3) {
      alert("No valid content found to generate the PDF.");
      return;
    }
  
    // Generate and download PDF
    const options = {
      margin: 0.5,
      filename: `askAIvan_Analysis_${date}.pdf`,
      image: { type: "jpeg", quality: 0.98 },
      html2canvas: { scale: 2, useCORS: true },
      jsPDF: { unit: "in", format: "a4", orientation: "portrait" },
    };
  
    html2pdf().from(contentContainer).set(options).save();
  };

  // Logout user
  // const handleLogout = () => {
  //   signOut(auth).catch((error) => console.error("Logout error:", error));
  // };

  return (
    <div style={dynamicStyles.container}>
      <Header />
      <div style={dynamicStyles.pageContainer}>

      <div style={dynamicStyles.pageWrapper}>
        <div style={styles.navContainer}>
          <button 
            onClick={() => { setActiveTab("new"); setSelectedAnalysis(null); }} 
            style={{ ...styles.tab, ...(activeTab === "new" ? styles.activeTab : {}) }}
          >
            Current
          </button>
          <button 
            onClick={() => { setActiveTab("saved"); setSelectedAnalysis(null); }} 
            style={{ ...styles.tab, ...(activeTab === "saved" ? styles.activeTab : {}), display: "flex", alignItems: "center", gap: "6px" }} // Align elements horizontally
          >
            Saved
            {savedAnalyses.length > 0 && (
              <span style={styles.badge}>{savedAnalyses.length}</span>
            )}
          </button>
        </div>

        {/* 🔹 Show Current Analysis Section */}
        {activeTab === "new" && (
          <div style={dynamicStyles.newAnalysisContainer}>

          {/* Big Idea Phase */}
          {phase === "big-idea" && (
            <div style={dynamicStyles.resultContainer}>
              <h1>What's your startup idea?</h1>
              <textarea
                placeholder="Describe your idea in 100 words or less."
                value={bigIdea}
                onChange={handleTextareaChange}
                style={dynamicStyles.textarea}
              />
              <p style={styles.guideText}>
                Include your
                <span style={styles.highlight}> product or solution </span>, the
                <span style={styles.highlight}> problem it solves </span>, its
                <span style={styles.highlight}> target customer </span> and any
                <span style={styles.highlight}> unique advantage </span> you have.
              </p>
              <p>
                Word count: {bigIdea.trim().split(/\s+/).filter(word => word).length}/{maxWords}
              </p>

              {/* Container to position the spinner over the button */}
              <div style={{ position: "relative", paddingBottom: "40px" }}>
                <button
                  onClick={async () => {
                    if (!bigIdea.trim()) {
                      setError("Please describe your startup idea.");
                      return;
                    }
                    setError(null);
                    setLoading(true); // Show spinner
                    try {
                      const initialQuestion = await generateNextQuestion(bigIdea, []);
                      setClarifyingQuestions([initialQuestion]);
                      setPhase("clarifying-questions");
                    } catch (error) {
                      setError("Error generating the first clarifying question.");
                      console.error(error);
                    } finally {
                      setLoading(false); // Hide spinner when done
                    }
                  }}
                  style={styles.submitButton}
                >
                  Submit
                </button>

                {/* Loading Spinner Overlay */}
                {loading && (
                  <div style={styles.spinnerContainer}>
                    <p style={styles.thinkingText}>Generating...</p>
                    <ClipLoader color="#3498db" size={50} />
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Clarifying Questions Phase */}
          {phase === "clarifying-questions" && (
            <div style={styles.resultContainer}>
              <h2>Tell me more...</h2>
              <p>{clarifyingQuestions[currentQuestion]}</p>
              <textarea
                placeholder="Your response here..."
                value={clarifyingResponses[currentQuestion] || ""}
                onChange={(e) =>
                  setClarifyingResponses({
                    ...clarifyingResponses,
                    [currentQuestion]: e.target.value,
                  })
                }
                style={dynamicStyles.textarea}
              />
              {/* Container to position the spinner over the button */}
              <div style={{ position: "relative", paddingBottom: "40px" }}>
                <button
                  onClick={async () => {
                    if (!clarifyingResponses[currentQuestion]?.trim()) {
                      setError("Please answer the question before proceeding.");
                      return;
                    }
                    setError(null);
                    setLoading(true); // Show spinner
                    try {
                      if (currentQuestion < maxQuestions - 1) {
                        const nextQuestion = await generateNextQuestion(
                          bigIdea,
                          clarifyingResponses
                        );
                        setClarifyingQuestions([...clarifyingQuestions, nextQuestion]);
                        setCurrentQuestion(currentQuestion + 1);
                      } else {
                        await handleBigIdeaSubmit({
                          bigIdea,
                          clarifyingResponses,
                        });
                      }
                    } catch (error) {
                      setError("Error generating the next clarifying question.");
                      console.error(error);
                    } finally {
                      setLoading(false); // Hide spinner when done
                    }
                  }}
                  style={styles.submitButton}
                >
                  {currentQuestion < maxQuestions - 1 ? "Next" : "Submit"}
                </button>

                {/* Loading Spinner Overlay */}
                {loading && (
                  <div style={styles.spinnerContainer}>
                    <p style={styles.thinkingText}>Generating...</p>
                    <ClipLoader color="#3498db" size={50} />
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Quick Analysis Phase */}
          {phase === "big-idea-analysis" && (
            <div style={styles.resultContainer}>
              <h2>Startup Concept Synopsis</h2>

              {quickAnalysis && (
                <div id="quickAnalysisContent" style={{
                  background: "#f9f9f9",
                  padding: "15px",
                  borderRadius: "8px",
                  boxShadow: "0px 4px 6px rgba(0, 0, 0, 0.1)",
                  lineHeight: "1.6"
                }}>
                  {formatStreamedText(quickAnalysis)}
                </div>
              )}

              {!loading && !quickAnalysis && <p>No insights generated yet. Please try again.</p>}

              {/* Button Container with Relative Positioning */}
              <div style={{ position: "relative", paddingBottom: "40px" }}>
                  <div style={dynamicStyles.buttonContainer}>
                    <button onClick={() => { setRefineFeedback(""); setRefineModalOpen(true); }} style={styles.redoButton}>
                      Refine Concept
                    </button>
                    <button onClick={() => setPhase("select-segment")} style={styles.submitButton}>
                      Dive Deeper
                    </button>
                    <button onClick={resetToBigIdea} style={styles.startOverButton}>
                      Start Over
                    </button>
                  </div>

                  {/* Loading Spinner Overlaying Buttons */}
                  {loading && (
                    <div style={styles.spinnerContainer}>
                      <p style={styles.thinkingText}>Responding...</p>
                      <ClipLoader color="#3498db" size={50} />
                    </div>
                  )}
                </div>
            </div>
          )}

          {/* Refine Modal */}
          {isRefineModalOpen && (
            <div style={styles.modalOverlay}>
              <div style={styles.modalContent}>
                <h2>What would you like to tweak?</h2>
                <textarea
                  placeholder="Enter your suggestions for refinement here..."
                  value={refineFeedback}
                  onChange={(e) => setRefineFeedback(e.target.value)}
                  style={dynamicStyles.textarea}
                />
                <div style={styles.buttonContainer}>
                  <button
                    onClick={() => setRefineModalOpen(false)}
                    style={styles.cancelButton}
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleRefineSubmit}
                    style={styles.submitButton}
                  >
                    Submit
                  </button>
                </div>
              </div>
            </div>
          )}
      
          {/* Select Launch Market Segment */}
          {phase === "select-segment" && (
            <div style={styles.resultContainer}>
              <h2>Select a Launch Market</h2>
              <p>Click on a customer segment below for deeper analysis:</p>

              {/* Wrapping container to control spinner positioning */}
              <div style={{ position: "relative", paddingBottom: "50px" }}>
                <ul style={styles.list}>
                  {segmentOptions.map((segment, index) => (
                    <li key={index} style={styles.listItem}>
                      <button
                        style={{
                          ...styles.submitButton,
                          fontSize: "18px",
                          padding: "10px 20px",
                          opacity: loading ? 0.6 : 1, // Dim buttons when loading
                          pointerEvents: loading ? "none" : "auto", // Prevent multiple clicks
                        }}
                        onClick={async () => {
                          setSelectedSegment(segment); // Set the selected segment
                          setLoading(true); // Start loading
                          try {
                            await generateSegmentQuestion(segment); // Fetch single clarifying question
                            setPhase("segment-question"); // Move to next phase
                          } catch (error) {
                            setError("Error generating segment question.");
                            console.error(error);
                          } finally {
                            setLoading(false); // Stop loading
                          }
                        }}
                      >
                        {segment}
                      </button>
                    </li>
                  ))}
                </ul>

                {/* Ensure Spinner Appears Directly Below Buttons */}
                {loading && (
                  <div style={styles.spinnerContainer}>
                    <p style={styles.thinkingText}>Fetching insights...</p>
                    <ClipLoader color="#3498db" size={50} />
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Segment Question Phase */}
          {phase === "segment-question" && (
            <div style={styles.resultContainer}>
              <h2>Segment Question</h2>
              <p>{segmentQuestion}</p>
              <textarea
                placeholder="Your response here..."
                value={segmentResponse}
                onChange={(e) => setSegmentResponse(e.target.value)}
                style={dynamicStyles.textarea}
              />
              {/* Container for Button & Spinner */}
              <div style={{ position: "relative", paddingBottom: "40px" }}>
                <button
                  onClick={async () => {
                    if (!segmentResponse.trim()) {
                      setError("Please provide a response before submitting.");
                      return;
                    }
                    setError(null);
                    setLoading(true); // Show spinner
                    try {
                      await handleSegmentResponseSubmit(); // Process response
                    } catch (error) {
                      setError("Error submitting segment response.");
                      console.error(error);
                    } finally {
                      setLoading(false); // Hide spinner when done
                    }
                  }}
                  style={{
                    ...styles.submitButton,
                    opacity: loading ? 0.6 : 1, // Dim button when loading
                    pointerEvents: loading ? "none" : "auto", // Disable clicks when loading
                  }}
                >
                  Submit Response
                </button>

                {/* Loading Spinner Overlay */}
                {loading && (
                  <div style={styles.spinnerContainer}>
                    <p style={styles.thinkingText}>Processing...</p>
                    <ClipLoader color="#3498db" size={50} />
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Deep Analysis Phase */}
          {phase === "insight" && (
            <div style={styles.resultContainer}>
              <h2>Segment Analysis - {selectedSegment}</h2>

              {deepAnalysis && (
                <div id="deepAnalysisContent" style={{
                  background: "#f9f9f9",
                  padding: "15px",
                  borderRadius: "8px",
                  boxShadow: "0px 4px 6px rgba(0, 0, 0, 0.1)",
                  lineHeight: "1.6"
                }}>
                  {formatStreamedText(deepAnalysis)}
                </div>
              )}

              {!loading && !deepAnalysis && <p>No insights generated yet. Please try again.</p>}

              {/* Button Container with Relative Positioning */}
              <div style={{ position: "relative", paddingBottom: "40px" }}>
                <div style={dynamicStyles.buttonContainer}>
                  <button onClick={() => { setSharpenFeedback(""); setSharpenModalOpen(true); }} style={styles.redoButton}>
                    Sharpen Analysis
                  </button>
                  <button onClick={generateValidationStrategy} style={styles.submitButton}>
                    Validate the Opportunity
                  </button>
                  <button
                    onClick={() => {
                      setValidationStrategy("");
                      setSegmentResponse("");
                      setSegmentQuestion("");
                      setSelectedSegment(null);
                      setDeepAnalysis("");
                      setPhase("big-idea-analysis");
                    }}
                    style={styles.redoButton}
                  >
                    Back to Synopsis
                  </button>
                </div>

                {/* Loading Spinner Overlaying Buttons */}
                {loading && (
                  <div style={styles.spinnerContainer}>
                    <p style={styles.thinkingText}>Responding...</p>
                    <ClipLoader color="#3498db" size={50} />
                  </div>
                )}
              </div>
            </div>
          )}
          
          {/* Sharpen Modal */}
          {isSharpenModalOpen && (
            <div style={styles.modalOverlay}>
              <div style={styles.modalContent}>
                <h2>What would you like to burnish?</h2>
                <textarea
                  placeholder="Enter your suggestions for improvement here..."
                  value={sharpenFeedback}
                  onChange={(e) => setSharpenFeedback(e.target.value)}
                  style={dynamicStyles.textarea}
                />
                <div style={styles.buttonContainer}>
                  <button
                    onClick={() => setSharpenModalOpen(false)}
                    style={styles.cancelButton}
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleSharpenSubmit}
                    style={styles.submitButton}
                  >
                    Submit
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Validation Strategy Phase */}
          {phase === "validation-strategy" && (
            <div style={styles.resultContainer}>
              <h2>Validation Strategy - {selectedSegment}</h2>


              {validationStrategy && (
                <div id="validationStrategyContent" style={{
                  background: "#f9f9f9",
                  padding: "15px",
                  borderRadius: "8px",
                  boxShadow: "0px 4px 6px rgba(0, 0, 0, 0.1)",
                  lineHeight: "1.6"
                }}>
                  {formatStreamedText(validationStrategy)}
                </div>
              )}

              {!loading && !validationStrategy && <p>No insights generated yet. Please try again.</p>}

              {/* Button Container with Relative Positioning */}
              <div style={{ position: "relative", paddingBottom: "40px" }}>
                <div style={dynamicStyles.buttonContainer}>
                  <button
                    onClick={() => {
                      setValidationStrategy("");
                      setSegmentResponse("");
                      setSegmentQuestion("");
                      setSelectedSegment(null);
                      setDeepAnalysis("");
                      setPhase("big-idea-analysis");
                    }}
                    style={styles.redoButton}
                  >
                    Back to Synopsis
                  </button>
                  <button
                    onClick={handleSaveAnalysis} // Call function to generate and save analysis
                    style={styles.submitButton}
                  >
                    Save Analysis
                  </button>
                  <button onClick={resetToBigIdea} style={styles.startOverButton}>
                    Start Over
                  </button>
                </div>

                {/* Loading Spinner Overlaying Buttons */}
                {loading && (
                  <div style={styles.spinnerContainer}>
                    <p style={styles.thinkingText}>Responding...</p>
                    <ClipLoader color="#3498db" size={50} />
                  </div>
                  )}
                </div>
            </div>
          )}
          </div>
        )}

        {/* 🔹 Show Full-Screen View for Selected Analysis */}
        {activeTab === "saved" && selectedAnalysis ? (
          <div style={dynamicStyles.fullScreenView}>
            <h2>{selectedAnalysis.title}</h2>

            {/* Accordion for Quick Analysis, Deep Analysis & Validation Strategy */}
            <div style={styles.accordionContainer}>
              {[
                { key: "quickAnalysis", title: "Quick Analysis", content: selectedAnalysis.quickAnalysis },
                { key: "deepAnalysis", title: "Deep Analysis", content: selectedAnalysis.deepAnalysis },
                { key: "validationStrategy", title: "Validation Strategy", content: selectedAnalysis.validationStrategy }
              ].map(({ key, title, content }) => (
                <div key={key} style={styles.accordionSection}>
                  <button
                    style={styles.accordionHeader}
                    onClick={() => setOpenSection(openSection === key ? null : key)}
                  >
                    {title} {openSection === key ? "▲" : "▼"}
                  </button>
                  {openSection === key && (
                    <div style={styles.accordionContent}>{formatSavedAnalysis(content)}</div>
                  )}
                </div>
              ))}
            </div>

            {/* 🔥 Button Container */}
            <div style={{ position: "relative", display: "inline-block", width: "100%" }}>
              <div style={dynamicStyles.buttonContainer}>
                <button onClick={() => setSelectedAnalysis(null)} style={styles.redoButton}>← List</button>
                <button onClick={handleDownloadPDF} style={styles.submitButton}>Download PDF</button>
                <button onClick={() => setConsultModalOpen(true)} style={styles.consultButton}>Request Consult</button>                
                <button onClick={() => handleDeleteAnalysis(selectedAnalysis.id)} style={styles.deleteButton}>Delete Analysis</button>
              </div>
            </div>
          </div>
        ) : (
          // 🔹 Show List of Saved Analyses if No Analysis is Selected
          activeTab === "saved" && (
            <div style={dynamicStyles.savedAnalysisContainer}>

            {savedAnalyses.length === 0 ? (
              <p>No saved analyses yet.</p>
            ) : (
              savedAnalyses.map((analysis) => (
                <div key={analysis.id} style={styles.analysisWrapper}>
                  {/* Analysis Card */}
                  <div 
                      onClick={() => {
                        setSelectedAnalysis(analysis); // ✅ Set the selected analysis
                        setOpenSection("quickAnalysis"); // ✅ Ensure Quick Analysis is expanded
                      }} 
                    style={styles.analysisCard}
                  >
                    <h3>{analysis.title}</h3>
                    <p style={{ fontStyle: "italic", color: "#888" }}>
                      {analysis.elevatorPitch !== "Generating pitch..." ? analysis.elevatorPitch : "Generating pitch..."}
                    </p>
                    <p style={{ color: "#007BFF", fontSize: "14px", marginTop: "8px", textAlign: "center" }}>
                      Tap to view more...
                    </p>
                  </div>
                </div>
              ))
            )}
            {/* ℹ️ Show message below the saved analyses */}
              <p style={styles.savedAnalysisInfo}>
                Save up to five deep dives on a first-in, first-out basis.
              </p>

            {/* 🔥 Loading Spinner Overlay (for saving) BELOW the existing saved analyses */}
            {loading && (
              <div style={styles.spinnerBelow}>
                <p style={styles.thinkingText}>Saving analysis...</p>
                <ClipLoader color="#3498db" size={50} />
              </div>
            )}

            </div>
          )
        )}
      </div>
      

        {/* Analysis Saved Acknowledgement Modal */}
        {isSavedModalOpen && (
          <div style={styles.modalOverlay}>
            <div style={styles.modalContent}>
              <h2>Analysis Summary Saved</h2>
              <p>Summary has been saved and is available in the Saved Analyses tab.</p>
              <button
                onClick={() => setIsSavedModalOpen(false)}
                style={styles.submitButton}
              >
                OK
              </button>
            </div>
          </div>
        )}

        {/* Request Consult Modal (Enter Campaign Code) */}
        {isConsultModalOpen && (
          <div style={styles.modalOverlay}>
            <div style={styles.modalContent}>
              <h2>Enter Request Code</h2>
              <input
                type="text"
                placeholder="Enter your consult code"
                value={campaignCode}
                onChange={(e) => setCampaignCode(e.target.value)}
                style={styles.input}
              />
              <div style={styles.buttonContainer}>
                <button
                  type="button"
                  onClick={() => {
                    setCampaignCode(""); // Clear input field
                    setConsultModalOpen(false); // Close modal
                  }}
                  style={styles.cancelButton}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={validateCampaign} // Calls validation function
                  style={styles.submitButton}
                >
                  Verify
                </button>
              </div>
              {isCampaignValid === false && <p style={{ color: "red" }}>Invalid or inactive campaign code. Please try again.</p>}
            </div>
          </div>
        )}
        
        {/* Error Modal for Invalid Campaign Code */}
        {showErrorModal && (
          <div style={styles.modalOverlay}>
            <div style={styles.modalContent}>
              <h2>Request Failed</h2>
              <p>{errorMessage}</p>
              <button 
                onClick={() => {
                  setShowErrorModal(false); // Close error modal
                  setConsultModalOpen(false); // Close request modal
                  setPhase("validation-strategy"); // Return to validation-strategy phase
                }} 
                style={styles.submitButton}
              >
                OK
              </button>
            </div>
          </div>
        )}

        {/* Consult Request Details Modal */}
        {isConsultDetailsModalOpen && (
          <div style={styles.modalOverlay}>
            <div style={styles.modalContent}>
              <h2 style={{ textAlign: "center", marginBottom: "15px" }}>Consultation Request</h2>

              <div style={{ display: "flex", flexDirection: "column", gap: "15px", alignItems: "flex-start" }}>
                
                {/* Name Field */}
                <div style={{ width: "100%" }}>
                  <label style={{ ...styles.label, textAlign: "left", display: "block" }}>Name</label>
                  <input
                    type="text"
                    placeholder="Enter your name"
                    value={userData.name}
                    onChange={(e) => setUserData({ ...userData, name: e.target.value })}
                    style={styles.input}
                    required
                  />
                </div>

                {/* Email Field */}
                <div style={{ width: "100%" }}>
                  <label style={{ ...styles.label, textAlign: "left", display: "block" }}>Email</label>
                  <input
                    type="email"
                    value={userData.email} // Pre-filled with logged-in email
                    disabled
                    style={{ ...styles.input, backgroundColor: "#f0f0f0", cursor: "not-allowed" }}
                    required
                  />
                </div>

                {/* Status Dropdown */}
                <div style={{ width: "100%" }}>
                  <label style={{ ...styles.label, textAlign: "left", display: "block" }}>Your Status</label>
                  <select
                    value={userData.status}
                    onChange={(e) => setUserData({ ...userData, status: e.target.value })}
                    style={styles.select}
                    required
                  >
                    <option value="undergraduate">Undergraduate</option>
                    <option value="graduate">Graduate Student / Researcher</option>
                    <option value="staff/faculty">IHL Staff / Faculty</option>
                    <option value="other">Other</option>
                  </select>
                </div>

                {/* Interview Experience / Launched */}
                <div style={{ width: "100%" }}>
                  <label style={{ ...styles.label, textAlign: "left", display: "block" }}>Have you luanched your startup?</label>
                  <div style={{ display: "flex", gap: "15px", marginTop: "5px" }}>
                    <label style={{ display: "flex", alignItems: "center", gap: "5px" }}>
                      <input
                        type="radio"
                        name="interviews"
                        value="yes"
                        checked={userData.interviews === "yes"}
                        onChange={() => setUserData({ ...userData, interviews: "yes" })}
                        required
                      />
                      Yes
                    </label>
                    <label style={{ display: "flex", alignItems: "center", gap: "5px" }}>
                      <input
                        type="radio"
                        name="interviews"
                        value="no"
                        checked={userData.interviews === "no"}
                        onChange={() => setUserData({ ...userData, interviews: "no" })}
                        required
                      />
                      No
                    </label>
                  </div>
                </div>

                {/* Button Container */}
                <div style={{ display: "flex", justifyContent: "space-between", marginTop: "20px", width: "100%" }}>
                  <button
                    type="button"
                    onClick={() => setConsultDetailsModalOpen(false)}
                    style={styles.cancelButton}
                  >
                    Cancel
                  </button>
                  <button
                    onClick={submitConsultRequest}
                    style={styles.submitButton}
                    disabled={isSubmitting} // 🔥 Disable button while submitting
                  >
                    {isSubmitting ? "Submitting..." : "Submit Request"}
                  </button>
                </div>

              </div>
            </div>
          </div>
        )}

        {/* Acknowledgment Modal */}
        {isAcknowledgementModalOpen && (
          <div style={styles.modalOverlay}>
            <div style={styles.modalContent}>
              <h2>Request Received</h2>
              <p>We will reach out to you soon.</p>
              <button
          onClick={() => {
            setCampaignCode(""); // Clear campaignCode when user closes modal
            setIsAcknowledgementModalOpen(false);
          }}
          style={styles.submitButton}
        >
          OK
        </button>
            </div>
          </div>
        )}

        {/* Feedback Form Modal */}
        {isFeedbackModalOpen && (
          <div style={styles.modalOverlay}>
            <div style={styles.modalContent}>
              <h2>Don't hold back...</h2>
              <form onSubmit={handleFeedbackSubmit}>
                <textarea class="textarea_two"
                  placeholder="How can AIVAN better support your ideation process?"
                  value={feedback}
                  onChange={(e) => setFeedback(e.target.value)}
                  required
                  style={dynamicStyles.textarea}
                />
                <input
                  type="text"
                  placeholder="Your Name"
                  value={fbName}
                  onChange={(e) => setFbName(e.target.value)}
                  required
                  style={styles.input}
                />
                <input
                  type="email"
                  placeholder="Your Email"
                  value={fbEmail}
                  onChange={(e) => setFbEmail(e.target.value)}
                  required
                  style={styles.input}
                />
                <select
                  value={fbRole}
                  onChange={(e) => setFbRole(e.target.value)}
                  required
                  style={styles.select}
                >
                  <option value="">Your role</option>
                  <option value="Founder">Founder</option>
                  <option value="Mentor">Mentor</option>
                  <option value="Incubator">Incubator</option>
                  <option value="Partner">Partner</option>
                  <option value="Other">Other</option>
                </select>
                <div style={styles.buttonContainer}>
                  <button
                    type="button"
                    onClick={() => setFeedbackModalOpen(false)}
                    style={styles.cancelButton}
                  >
                    Cancel
                  </button>
                  <button type="submit" style={styles.submitButton}>
                    Submit
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Success Modal */}
        {isSuccessModalOpen && (
          <div style={styles.modalOverlay}>
            <div style={styles.modalContent}>
              <h2>Thank You!</h2>
              <p>Your feedback has been successfully submitted.</p>
              <button
                onClick={() => setSuccessModalOpen(false)}
                style={styles.submitButton}
              >
                Close
              </button>
            </div>
          </div>
        )}

        {/* Feedback Trigger Link */}
        <div style={styles.feedbackContainer}>
          <span style={styles.feedbackText}>Can AIVAN do better? </span>
          <span
            onClick={() => setFeedbackModalOpen(true)}
            style={{
              color: "red", // Red text
              textDecoration: "underline", // Underlined like a link
              cursor: "pointer", // Clickable pointer
              fontWeight: "bold",
              marginLeft: "5px", // Add spacing from previous text
            }}
          >
            Tell us how...
          </span>
        </div>

      </div> {/* End of page container */}
      {/* <Footer /> */}
    </div> // End of main container
  );
}

export default PromptFlow;